import { c as _c } from "react/compiler-runtime";
import { CircularProgress, ToggleGroup } from "@flat-ui";
import { Box } from "@mui-wrapper";
import { appStateSelector } from "@store/app/app-selector";
import { fetchingUserSettingSelector, userSettingsSelector } from "@store/settings/settings-selector";
import { updateUserSetting } from "@store/settings/settings-thunks";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { loggedInUserSelector } from "@store/user/user-selector";
import { useEffect, useState } from "react";

/** Available theme mode options shown in the toggle */
const THEME_MODE_ITEMS = [{
  label: "Light",
  value: "light"
}, {
  label: "Dark",
  value: "dark"
}, {
  label: "System",
  value: "system"
}];

/**
 * Theme mode toggle allowing the user to choose between light, dark, and system themes.
 *
 * Dark mode is a beta feature: when beta testing is disabled the toggle is locked to
 * "light" and the options cannot be changed. Otherwise the selected value is persisted
 * as the user's `theme_mode` setting and applied across the app.
 */
export function ThemeModeToggle() {
  const $ = _c(22);
  const dispatch = useAppDispatch();
  const loggedInUser = useAppSelector(loggedInUserSelector);
  const {
    isBetaTestingEnabled
  } = useAppSelector(appStateSelector);
  const {
    theme_mode: themeModeSetting
  } = useAppSelector(userSettingsSelector);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = fetchingUserSettingSelector("theme_mode");
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const fetchingThemeModeSettings = useAppSelector(t0);
  const savedThemeMode = isBetaTestingEnabled ? themeModeSetting?.value ?? "system" : "light";
  const [selectedThemeMode, setSelectedThemeMode] = useState(savedThemeMode);
  let t1;
  let t2;
  if ($[1] !== savedThemeMode) {
    t1 = () => {
      setSelectedThemeMode(savedThemeMode);
    };
    t2 = [savedThemeMode];
    $[1] = savedThemeMode;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  if ($[4] !== dispatch || $[5] !== isBetaTestingEnabled || $[6] !== loggedInUser || $[7] !== themeModeSetting?.modifiedAt) {
    t3 = async function handleThemeChange(newThemeMode) {
      if (!isBetaTestingEnabled || !loggedInUser) {
        return;
      }
      setSelectedThemeMode(newThemeMode);
      await dispatch(updateUserSetting({
        identifier: "theme_mode",
        userId: loggedInUser.id,
        payload: {
          value: newThemeMode,
          modifiedAt: themeModeSetting?.modifiedAt ?? ""
        }
      })).unwrap();
    };
    $[4] = dispatch;
    $[5] = isBetaTestingEnabled;
    $[6] = loggedInUser;
    $[7] = themeModeSetting?.modifiedAt;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const handleThemeChange = t3;
  let t4;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      position: "relative",
      display: "inline-flex"
    };
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const t5 = !isBetaTestingEnabled || fetchingThemeModeSettings === "pending";
  const t6 = fetchingThemeModeSettings === "pending" ? "hidden" : "visible";
  let t7;
  if ($[10] !== t6) {
    t7 = {
      visibility: t6
    };
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== handleThemeChange || $[13] !== selectedThemeMode || $[14] !== t5 || $[15] !== t7) {
    t8 = <ToggleGroup items={THEME_MODE_ITEMS} value={selectedThemeMode} onChange={handleThemeChange} isDisabled={t5} sxGroup={t7} />;
    $[12] = handleThemeChange;
    $[13] = selectedThemeMode;
    $[14] = t5;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== fetchingThemeModeSettings) {
    t9 = fetchingThemeModeSettings === "pending" && <CircularProgress size="1.25rem" sx={{
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)"
    }} />;
    $[17] = fetchingThemeModeSettings;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== t8 || $[20] !== t9) {
    t10 = <Box sx={t4}>{t8}{t9}</Box>;
    $[19] = t8;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  return t10;
}